import React, { useEffect, useRef, useState } from 'react';

// "Elevate Your Wellness" text ke saath video (1:1 square).
// • The Elevate video is always uploaded converted to 1:1 (the Admin Panel converts it itself),
//   so the box always stays square.
// • Default: autoplay + MUTE (awaaz band) — browser ke rules ke mutabiq.
// • The video STOPS by itself after 3 plays (no loop) — then the visitor can play it manually.
// • If the visitor plays/pauses or seeks mid-way, control passes to them (auto-replay off).
// • Visitor apni marzi se: sound on/off, play/pause aur seek bar se aage-peechay kar sakta hai.
// • Speed: the video loads AFTER the page loads, and only when the section comes near the screen.
//   Until then a light poster shows; the box's 1:1 size is reserved in advance (no layout jump).
// Uploaded from Admin Panel → 🎞️ Elevate Video (the video is auto-converted to 1:1 + compressed there, now with sound).

const fmt = (s) => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r < 10 ? '0' : ''}${r}`;
};

export default function AboutMedia({ url, type, poster, duration, loading }) {
  const boxRef = useRef(null);
  const videoRef = useRef(null);
  const [nearScreen, setNearScreen] = useState(false);
  const [pageReady, setPageReady] = useState(typeof document !== 'undefined' && document.readyState === 'complete');
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(Number(duration) > 0 ? Number(duration) : 0);
  const [scrub, setScrub] = useState(false);
  // 3-play limit: count full autoplay plays; if the visitor plays/pauses/seeks manually, control is theirs
  const playCountRef = useRef(0);
  const autoRef = useRef(true);

  // Only fetch the video data after the page fully loads (for LCP / speed for users coming from Meta ads)
  useEffect(() => {
    if (pageReady) return undefined;
    const onLoad = () => setTimeout(() => setPageReady(true), 600);
    window.addEventListener('load', onLoad, { once: true });
    return () => window.removeEventListener('load', onLoad);
  }, [pageReady]);

  useEffect(() => {
    if (!url) return undefined;
    const el = boxRef.current;
    if (!el || !('IntersectionObserver' in window)) { setNearScreen(true); return undefined; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setNearScreen(true); io.disconnect(); }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [url]);

  const show = nearScreen && pageReady;

  useEffect(() => {
    const v = videoRef.current;
    if (show && v) {
      v.muted = true; // required for autoplay
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, [show, url]);

  // URL not here yet: while the config loads, keep the space reserved
  // placeholder box (no layout shift when the video arrives); otherwise nothing.
  if (!url) {
    if (!loading) return null;
    return (
      <div className="about-media about-media-loading" aria-hidden="true">
        <div className="about-media-shimmer" />
      </div>
    );
  }

  const v = () => videoRef.current;

  const onLoadedMeta = () => {
    const el = v();
    if (!el) return;
    if (isFinite(el.duration) && el.duration > 0) {
      setDur(el.duration);
    } else if (!(dur > 0)) {
      // Browser-recorded files have no length written (Infinity) — this trick reveals it
      const fix = () => {
        el.removeEventListener('timeupdate', fix);
        if (isFinite(el.duration)) setDur(el.duration);
        el.currentTime = 0;
      };
      el.addEventListener('timeupdate', fix);
      try { el.currentTime = 1e101; } catch (_) { /* ignore */ }
    }
  };

  const onTimeUpdate = () => {
    const el = v();
    if (!el || scrub) return;
    setTime(el.currentTime || 0);
    if (isFinite(el.duration) && el.duration > 0 && Math.abs(el.duration - dur) > 0.3) setDur(el.duration);
  };

  const togglePlay = () => {
    const el = v();
    if (!el) return;
    autoRef.current = false; // visitor pressed it manually — their choice now
    if (el.paused) { const p = el.play(); if (p && p.catch) p.catch(() => {}); } else el.pause();
  };

  // After 3 full autoplay plays the video stops (back to start); then the visitor runs it manually.
  const onEnded = () => {
    const el = v();
    if (!el || !autoRef.current) return;
    if (playCountRef.current < 2) {
      playCountRef.current += 1;
      const p = el.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      autoRef.current = false;
      try { el.currentTime = 0; } catch (_) { /* ignore */ }
      el.pause();
    }
  };

  const toggleMute = () => {
    const el = v();
    if (!el) return;
    const next = !el.muted;
    el.muted = next;
    if (!next) { el.volume = 1; if (el.paused) { const p = el.play(); if (p && p.catch) p.catch(() => {}); } }
    setMuted(next);
  };

  const seekTo = (val) => {
    const el = v();
    if (!el) return;
    autoRef.current = false; // visitor ne seek kiya — ab uski marzi
    const t = Math.max(0, Math.min(Number(val), dur || Number(val)));
    setTime(t);
    try { el.currentTime = t; } catch (_) { /* ignore */ }
  };

  const isVideo = type !== 'image';

  return (
    <div className="about-media" ref={boxRef}>
      {!show && poster && <img src={poster} alt="" loading="lazy" decoding="async" />}
      {show && !isVideo && <img src={url} alt="Organic Slim Fit Tea" loading="lazy" decoding="async" />}
      {show && isVideo && (
        <>
          <video
            ref={videoRef}
            src={url}
            poster={poster || undefined}
            autoPlay muted playsInline
            preload="metadata"
            disablePictureInPicture
            onLoadedMetadata={onLoadedMeta}
            onTimeUpdate={onTimeUpdate}
            onEnded={onEnded}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onClick={togglePlay}
          />
          <div className="am-controls" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="am-btn" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M7 4.5v15a1 1 0 0 0 1.5.86l12-7.5a1 1 0 0 0 0-1.72l-12-7.5A1 1 0 0 0 7 4.5z" /></svg>
              )}
            </button>
            <input
              className="am-seek"
              type="range"
              min="0"
              max={dur > 0 ? dur : 100}
              step="0.05"
              value={dur > 0 ? Math.min(time, dur) : 0}
              disabled={!(dur > 0)}
              onChange={(e) => seekTo(e.target.value)}
              onPointerDown={() => setScrub(true)}
              onPointerUp={() => setScrub(false)}
              onKeyDown={() => setScrub(false)}
              aria-label="Seek video"
              style={{ '--am-pct': `${dur > 0 ? (Math.min(time, dur) / dur) * 100 : 0}%` }}
            />
            <span className="am-time">{fmt(time)}{dur > 0 ? ` / ${fmt(dur)}` : ''}</span>
            <button type="button" className={`am-btn am-sound${muted ? ' is-muted' : ''}`} onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
              {muted ? (
                <svg viewBox="0 0 24 24" width="19" height="19" fill="currentColor"><path d="M4 9v6h4l5 4V5L8 9H4z" /><path d="M16 9.5l5 5M21 9.5l-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" width="19" height="19" fill="currentColor"><path d="M4 9v6h4l5 4V5L8 9H4z" /><path d="M16 8.5a5 5 0 0 1 0 7M18.6 6a8.5 8.5 0 0 1 0 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" /></svg>
              )}
            </button>
          </div>
          {muted && (
            <button type="button" className="am-tap-sound" onClick={toggleMute}>🔊 Tap for sound</button>
          )}
        </>
      )}
    </div>
  );
}
